import { Box, Grid } from '@mui/material'
import { type Participant, type ZoomMediaStream } from '@fireflyhealth/zoom-web'
import React from 'react'
import { SessionVideoPlayer } from './SessionVideoPlayer'

/**
 * Props interface for the SessionParticipantsVideoPlayer component
 * Defines the required properties for rendering multiple participant video streams
 */
interface SessionParticipantsVideoPlayerProps {
  /** Zoom Stream API instance for managing video/audio streams */
  stream: ZoomMediaStream
  /** Array of participant objects representing users in the session */
  users: Participant[]
  /** Object mapping Zoom user ids to their network quality scores (0-3, where 3 is best quality) */
  networkQuality: Record<number, number>
}

/**
 * SessionParticipantsVideoPlayer Component
 *
 * A responsive grid layout component that renders video streams for all participants
 * in a Zoom session. This component handles the dynamic layout of participant videos
 * and can be used for both other participants and the current user's video.
 *
 * Layout behavior:
 * - Uses Material-UI Grid system with flexible item sizing
 * - Grid items automatically adjust based on content and available space
 * - Spacing of 2 units between grid items for visual separation
 * - Justified to flex-end for right-aligned layout (useful for self-video positioning)
 * - Each participant gets their own Box container with full height/width and relative positioning
 *
 * The component passes through the stream API and network quality data to individual
 * SessionVideoPlayer components for each participant, along with the isSelfVideo flag
 * to enable different rendering behaviors for the current user vs other participants.
 */
export const SessionParticipantsVideoPlayer: React.FC<SessionParticipantsVideoPlayerProps> = ({
  stream,
  users,
  networkQuality,
}) => {
  return (
    <>
      {/* Render each participant's video stream */}
      <Grid
        container
        spacing={2}
        style={{ backgroundColor: 'transparent' }}
        sx={{ justifyContent: 'flex-end' }}
      >
        {users.map(user => {
          return (
            <Grid key={user.userId}>
              <Box
                key={user.userId}
                sx={{
                  height: '100%',
                  width: '100%',
                  position: 'relative',
                }}
              >
                <SessionVideoPlayer
                  key={user.userId}
                  user={user}
                  stream={stream}
                  networkQuality={networkQuality[user.userId]} // Pass user-specific network quality
                  isSpotlightVideo={false}
                />
              </Box>
            </Grid>
          )
        })}
      </Grid>
    </>
  )
}
